import { useState, useEffect, useCallback, useRef } from 'react';
import { useBrand } from '../lib/brand-context.js';
import { apiGet, apiFetch } from '../lib/api.js';
import { apiPatch, apiPost } from '../lib/api-mutations.js';
import { useToast } from '../hooks/useToast.js';
import { Icon, Spinner } from '../components/ui/Icon.js';
import { CostPreview } from '../components/CostPreview.js';
import { Select } from '../components/Select.js';
import { Region, RegionHead } from '../components/ui/Region.js';
import { Chip } from '../components/ui/Chip.js';
import { Button } from '../components/ui/Button.js';
import { Plate } from '../components/ui/Plate.js';
import { Segmented } from '../components/ui/Segmented.js';
import type {
  CollectionPreviewResponse,
  CollectionPreview,
  Brand,
  SchedulerStatus,
  TenantResponse,
} from '../types/index.js';

type Cadence = 'manual' | 'weekly';

interface BrandOverviewRow {
  id: string;
  name: string;
  activePrompts: number;
  lastCollection: string | null;
  status: 'idle' | 'running' | 'scheduled' | 'completed' | 'failed' | 'stalled';
  scheduledAt: string | null;
}

interface SampleOption {
  value: number;
  label: string;
}

const SAMPLE_OPTIONS: SampleOption[] = [
  { value: 1, label: 'Minimal (single observation)' },
  { value: 5, label: 'Directional (\u00b120pp accuracy)' },
  { value: 10, label: 'Directional (\u00b120pp accuracy)' },
  { value: 20, label: 'Directional \u2014 recommended MVP (\u00b120pp accuracy)' },
  { value: 50, label: 'Operational (\u00b110pp accuracy)' },
];

const CONFIDENCE_TABLE = [
  { range: '10\u201320', tier: 'Directional', accuracy: '\u00b120pp' },
  { range: '50\u2013100', tier: 'Operational', accuracy: '\u00b110pp' },
  { range: '400+', tier: 'High', accuracy: '\u00b15pp' },
];

const CADENCE_OPTIONS = [
  { value: 'manual', label: 'Manual' },
  { value: 'weekly', label: 'Weekly' },
] as const;

export function SchedulePage() {
  const { brands, user, activeTenantId } = useBrand();
  const { addToast } = useToast();

  const [preview, setPreview] = useState<CollectionPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [cadence, setCadence] = useState<Cadence>('manual');
  const [cadenceLoading, setCadenceLoading] = useState(false);
  const [collecting, setCollecting] = useState(false);
  const [brandRows, setBrandRows] = useState<BrandOverviewRow[]>([]);
  const [brandRowsLoading, setBrandRowsLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Scheduler status
  const [schedulerStatus, setSchedulerStatus] = useState<SchedulerStatus | null>(null);
  const [schedulerLoading, setSchedulerLoading] = useState(true);

  // Samples per prompt
  const [samplesPerPrompt, setSamplesPerPrompt] = useState<number>(1);
  const [savedSamplesPerPrompt, setSavedSamplesPerPrompt] = useState<number>(1);
  const [samplesLoading, setSamplesLoading] = useState(true);
  const [samplesSaving, setSamplesSaving] = useState(false);

  // Confidence table expand
  const [showConfidence, setShowConfidence] = useState(false);

  // Spec 076: prefer a brand's tenant_id (they all share one tenant), fall
  // back to the context's activeTenantId. `user` is still used for gating.
  void user;
  const tenantId = brands[0]?.tenant_id ?? activeTenantId ?? undefined;

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, []);

  // Close confirm popover on outside click
  useEffect(() => {
    if (!showConfirm) return;
    function handleClick(e: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setShowConfirm(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showConfirm]);

  // Fetch tenant-wide preview (all brands)
  const fetchPreview = useCallback(async () => {
    if (!tenantId) return;
    setPreviewLoading(true);
    try {
      const res = await apiGet<CollectionPreviewResponse>(`/api/v1/tenants/${tenantId}/collection/preview`);
      setPreview(res.data);
    } catch {
      // silent
    } finally {
      setPreviewLoading(false);
    }
  }, [tenantId]);

  // Build brand overview rows from preview + per-brand job stats
  const fetchBrandRows = useCallback(async () => {
    if (!tenantId || brands.length === 0) return;
    setBrandRowsLoading(true);
    try {
      const previewRes = await apiGet<CollectionPreviewResponse>(`/api/v1/tenants/${tenantId}/collection/preview`);
      const previewData = previewRes.data;

      const rows: BrandOverviewRow[] = await Promise.all(
        brands.map(async (brand: Brand) => {
          const previewBrand = previewData.brands.find((b) => b.id === brand.id);
          const tc = previewBrand?.template_count ?? 0;
          // Backend-computed honest stall flag for this brand's latest run.
          const collectionStalled = previewBrand?.collection_stalled === true;

          // Fetch latest query jobs for this brand to derive last collection + status
          let lastCollection: string | null = null;
          let status: BrandOverviewRow['status'] = 'idle';
          let scheduledAt: string | null = null;
          try {
            const jobsRes = await apiGet<{ data: Array<{ status: string; executed_at: string | null; created_at: string; scheduled_at: string | null }> }>(
              `/api/v1/tenants/${tenantId}/brands/${brand.id}/queries?limit=1`,
            );
            const latestJob = jobsRes.data?.[0];
            if (latestJob) {
              lastCollection = latestJob.executed_at ?? latestJob.created_at;
              scheduledAt = latestJob.scheduled_at;
              if (latestJob.status === 'pending') {
                // A pending job scheduled for a future time hasn't run yet — it
                // is "Scheduled", not "Running". Only a pending job that is
                // eligible now (scheduled_at <= now, or missing) reads as active.
                const futureScheduled =
                  latestJob.scheduled_at != null &&
                  new Date(latestJob.scheduled_at).getTime() > Date.now();
                status = futureScheduled ? 'scheduled' : 'running';
              } else if (latestJob.status === 'completed') status = 'completed';
              else if (latestJob.status === 'failed') status = 'failed';
            }
          } catch {
            // If per-brand query fails, leave defaults
          }

          // A stalled run must not read as healthy "Running". The backend flag
          // overrides the status mapping above (it is only true when a run is
          // genuinely stuck — never for future-scheduled jobs, so no conflict).
          // Ordered last so 'stalled' always wins.
          if (collectionStalled) status = 'stalled';

          return {
            id: brand.id,
            name: brand.name,
            activePrompts: tc,
            lastCollection,
            status,
            scheduledAt,
          };
        }),
      );

      setBrandRows(rows);
    } catch {
      // silent
    } finally {
      setBrandRowsLoading(false);
    }
  }, [tenantId, brands]);

  // Fetch scheduler status — reconcile persisted cadence with in-memory state.
  // Scaleway can cold-start the container, which resets the in-process scheduler
  // to stopped even though the tenant setting says 'weekly'. If we detect that
  // mismatch, auto-restart the scheduler so the user doesn't have to re-click.
  const fetchSchedulerStatus = useCallback(async () => {
    try {
      const status = await apiGet<SchedulerStatus>('/api/v1/admin/scheduler/status');
      setSchedulerStatus(status);

      // Check persisted cadence from tenant settings
      let persistedCadence: Cadence = 'manual';
      if (tenantId) {
        try {
          const tenant = await apiGet<TenantResponse>(`/api/v1/tenants/${tenantId}`);
          const settings = tenant.data.settings as Record<string, unknown> | undefined;
          if (settings?.cadence === 'weekly') persistedCadence = 'weekly';
        } catch { /* silent */ }
      }

      if (persistedCadence === 'weekly' && !status.running) {
        // Scheduler should be running but isn't (container restart). Re-start it.
        try {
          await apiFetch('/api/v1/admin/scheduler/start', { method: 'POST' });
          const refreshed = await apiGet<SchedulerStatus>('/api/v1/admin/scheduler/status');
          setSchedulerStatus(refreshed);
        } catch { /* silent — manual click still works */ }
      }

      setCadence(persistedCadence);
    } catch {
      setCadence('manual');
    } finally {
      setSchedulerLoading(false);
    }
  }, [tenantId]);

  // Fetch tenant settings for samples_per_prompt
  const fetchTenantSettings = useCallback(async () => {
    if (!tenantId) return;
    try {
      const tenant = await apiGet<TenantResponse>(`/api/v1/tenants/${tenantId}`);
      const settings = tenant.data.settings;
      const saved = (settings as Record<string, unknown>).samples_per_prompt;
      const value = typeof saved === 'number' && saved > 0 ? saved : 1;
      setSamplesPerPrompt(value);
      setSavedSamplesPerPrompt(value);
    } catch {
      // silent — defaults to 1
    } finally {
      setSamplesLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchPreview();
    fetchBrandRows();
    fetchSchedulerStatus();
    fetchTenantSettings();
  }, [fetchPreview, fetchBrandRows, fetchSchedulerStatus, fetchTenantSettings]);

  // Handle cadence change
  const handleCadenceChange = async (newCadence: Cadence) => {
    if (newCadence === cadence || cadenceLoading) return;
    setCadenceLoading(true);

    try {
      if (newCadence === 'weekly') {
        // Start the scheduler
        await apiPost<unknown>('/api/v1/admin/scheduler/start', {});
        if (tenantId) {
          await apiPatch<unknown>(`/api/v1/tenants/${tenantId}`, {
            settings: { cadence: 'weekly' },
          });
        }
        addToast('Scheduler started \u2014 will run weekly', 'success');
      } else {
        // Stop the scheduler
        await apiPost<unknown>('/api/v1/admin/scheduler/stop', {});
        if (tenantId) {
          await apiPatch<unknown>(`/api/v1/tenants/${tenantId}`, {
            settings: { cadence: 'manual' },
          });
        }
        addToast('Scheduler stopped', 'success');
      }

      setCadence(newCadence);
      // Refresh scheduler status
      await fetchSchedulerStatus();
    } catch (err) {
      addToast(
        err instanceof Error ? err.message : 'Failed to update schedule',
        'error',
      );
    } finally {
      setCadenceLoading(false);
    }
  };

  // Save samples per prompt
  const handleSaveSamples = async () => {
    if (!tenantId || samplesSaving) return;
    setSamplesSaving(true);

    try {
      await apiPatch<unknown>(`/api/v1/tenants/${tenantId}`, {
        settings: {
          samples_per_prompt: samplesPerPrompt,
          samples_per_prompt_per_engine_per_week: samplesPerPrompt,
        },
      });
      setSavedSamplesPerPrompt(samplesPerPrompt);
      addToast(`Sampling updated to ${samplesPerPrompt} samples per prompt`, 'success');
      // Re-fetch preview since cost depends on sample count
      await fetchPreview();
    } catch (err) {
      addToast(
        err instanceof Error ? err.message : 'Failed to save sampling settings',
        'error',
      );
    } finally {
      setSamplesSaving(false);
    }
  };

  const handleCollectAll = async () => {
    setShowConfirm(false);
    setCollecting(true);
    try {
      const res = await apiFetch('/api/v1/admin/scheduler/trigger', {
        method: 'POST',
        body: JSON.stringify({ scope: 'all' }),
      });
      if (!res.ok) throw new Error('Trigger failed');
      const result = await res.json() as { data: { jobsCreated?: number; jobs_created?: number } };
      const jobsCreated = result.data?.jobsCreated ?? result.data?.jobs_created ?? 0;
      addToast(`Collection started for all brands: ${jobsCreated} queries scheduled`, 'success');

      // Start polling for completion
      let pollCount = 0;
      const maxPolls = 60;
      pollingRef.current = setInterval(async () => {
        pollCount++;
        try {
          if (tenantId) {
            const histRes = await apiFetch(`/api/v1/tenants/${tenantId}/collection/history?limit=1`);
            if (histRes.ok) {
              const histData = await histRes.json() as { data: Array<{ jobs_pending: number; jobs_completed: number }> };
              const latest = histData.data?.[0];
              if (latest && latest.jobs_pending === 0 && latest.jobs_completed > 0 && pollCount > 2) {
                if (pollingRef.current) {
                  clearInterval(pollingRef.current);
                  pollingRef.current = null;
                }
                setCollecting(false);
                addToast(`Collection complete: ${latest.jobs_completed} queries finished`, 'success');
                fetchPreview();
                fetchBrandRows();
                return;
              }
            }
          }
        } catch {
          // ignore
        }
        if (pollCount >= maxPolls) {
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          setCollecting(false);
          addToast('Collection polling timed out \u2014 jobs may still be processing', 'error');
        }
      }, 5000);
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to trigger collection', 'error');
      setCollecting(false);
    }
  };

  // Compute weekly cost projection from preview
  const weeklyCostProjection = preview
    ? `At ${samplesPerPrompt} sample${samplesPerPrompt !== 1 ? 's' : ''} \u00d7 ${preview.templates.length} prompts \u00d7 ${preview.engines.length} engines, weekly cost is approximately $${preview.estimated_cost.toFixed(2)} across all brands.`
    : null;

  const hasSamplesChanged = samplesPerPrompt !== savedSamplesPerPrompt;

  if (!user) {
    return null;
  }

  return (
    /* Spec 236 — the DASHBOARD archetype. Two configuration regions, the
       scheduler's own status, the four-column brands table, then the cost
       preview: summary first, then detail, regions that touch with a hairline
       between them.

       This is the most constrained file in either spec, and none of what
       constrains it is decoration. Six anchors across two wiring contracts run
       through the brands table below — the literal strings 'stalled' and
       'scheduled', the testids brand-status-stalled and
       brand-status-scheduled, and the fields collection_stalled and
       scheduled_at (gates 140.A.3 and 147.A.2) — plus this file's share of the
       native-element sum in gate 232.A.5. A restyle passes straight through
       all of them, which is exactly why each was grepped before committing
       rather than after. */
    <div data-testid="schedule-page" className="hub-page--dashboard" data-density="compact">
      <Region>
        <div className="hub-page__head">
          <div>
            <h1 className="hub-page__title">Schedule</h1>
            <p className="mt-1 text-sm text-[var(--hub-color-ink-muted)]">
              Manage collection schedules across all brands
            </p>
          </div>

          {/* Collect All — the one action this page exists for, so it is the
              single ink block. */}
          <div className="relative" ref={popoverRef}>
            <Button
              variant="primary"
              onClick={() => setShowConfirm(true)}
              disabled={collecting || !preview}
              data-testid="collect-all-btn"
            >
              {collecting ? <Spinner  /> : <Icon name="bx-play" />}
              Collect all brands
            </Button>

            {showConfirm && preview && (
              <Plate className="absolute right-0 top-full z-50 mt-2 w-80">
                <p className="text-sm text-[var(--hub-color-ink)]">
                  This will run <span className="font-semibold">{preview.total_queries} queries</span> across{' '}
                  <span className="font-semibold">{preview.brands.length} brands</span> costing
                  ~<span className="font-semibold">${preview.estimated_cost.toFixed(2)}</span>.
                </p>
                <div className="mt-3 flex gap-2 justify-end">
                  <Button variant="quiet" onClick={() => setShowConfirm(false)}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleCollectAll}
                    data-testid="collect-all-confirm-btn"
                  >
                    Proceed
                  </Button>
                </div>
              </Plate>
            )}
          </div>
        </div>
      </Region>

      {/* Schedule configuration */}
      <Region data-testid="schedule-config">
        <RegionHead
          title="Schedule configuration"
          caption="Set the collection cadence for all brands"
        />

        <div className="flex flex-wrap items-center gap-4">
          <div className="flex gap-2">
            {schedulerLoading ? (
              <div className="flex items-center gap-2 py-2 text-sm text-[var(--hub-color-ink-muted)]">
                <Spinner  /> Loading scheduler...
              </div>
            ) : (
              /* Spec 249 (review P1-11): cadence is a PARAMETER of one
                 schedule, so it is Kridt's Segmented control — a radiogroup,
                 one tab stop, arrow keys — not two free-standing 8px boxes.
                 The options keep their testids (cadence-manual/-weekly). */
              <Segmented
                options={CADENCE_OPTIONS}
                value={cadence}
                onChange={handleCadenceChange}
                label="Collection cadence"
                testId="cadence"
                disabled={cadenceLoading}
              />
            )}
          </div>

          {/* Scheduler status. Spec 235's mapping: emerald \u2192 ok, slate is not
              a tone at all but a bare chip for a genuinely neutral state. */}
          {!schedulerLoading && schedulerStatus && (
            <div className="flex items-center gap-3">
              {schedulerStatus.running ? (
                <Chip tone="ok">
                  Running
                  {schedulerStatus.nextRun && (
                    <>
                      {' \u2014 next '}
                      {new Date(schedulerStatus.nextRun).toLocaleString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </>
                  )}
                </Chip>
              ) : (
                <Chip>Stopped</Chip>
              )}
            </div>
          )}
        </div>

        {/* Last run info */}
        {!schedulerLoading && schedulerStatus?.lastRun && (
          <p className="mt-2 text-xs text-[var(--hub-color-ink-muted)]">
            <Icon name="bx-time-five" className="mr-1 inline" />
            Last run: {new Date(schedulerStatus.lastRun).toLocaleString(undefined, {
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
            {schedulerStatus.lastRunStats && (
              <> &mdash; {schedulerStatus.lastRunStats.jobsCreated} jobs created</>
            )}
          </p>
        )}

        {cadence === 'weekly' && (
          <p className="mt-2 text-xs text-[var(--hub-color-ink-muted)]">
            <Icon name="bx-time-five" className="mr-1 inline" />
            Runs every 7 days (checks hourly for due work)
          </p>
        )}
      </Region>

      {/* Statistical Sampling configuration. `samples-select` is a testid on a
          NON-NATIVE control and stays that way — gate 232.A.5 sums the native
          element across five files and requires zero, and gates 232.A.5 and
          118.A.4 both additionally assert the string still ships in the served
          bundle. */}
      <Region data-testid="sampling-config">
        <RegionHead
          title="Statistical sampling"
          caption="How many samples are collected per prompt per engine per week. Higher sample counts improve statistical confidence."
        />

        <div className="hub-toolbar">
          <div className="flex-1 max-w-xs">
            <label
              htmlFor="samples-select"
              className="hub-field__label mb-1.5 block"
            >
              Samples per prompt per engine per week
            </label>
            {samplesLoading ? (
              <div className="flex items-center gap-2 py-2 text-sm text-[var(--hub-color-ink-muted)]">
                <Spinner  /> Loading...
              </div>
            ) : (
              <Select
                options={SAMPLE_OPTIONS.map((opt) => ({
                  value: String(opt.value),
                  label: `${opt.value} — ${opt.label}`,
                }))}
                value={String(samplesPerPrompt)}
                onChange={(v) => setSamplesPerPrompt(Number(v))}
                className="w-full"
                data-testid="samples-select"
              />
            )}
          </div>

          <Button
            variant="ghost"
            onClick={handleSaveSamples}
            disabled={samplesSaving || !hasSamplesChanged || samplesLoading}
            data-testid="save-samples-btn"
          >
            {samplesSaving ? (
              <Spinner  />
            ) : (
              <Icon name="bx-save" />
            )}
            Save
          </Button>
        </div>

        {/* Weekly cost projection */}
        {weeklyCostProjection && (
          <p className="mt-3 text-xs text-[var(--hub-color-ink-muted)]" data-testid="weekly-cost-projection">
            {weeklyCostProjection}
          </p>
        )}

        {/* Confidence table toggle */}
        <div className="mt-4">
          <Button
            variant="quiet"
            onClick={() => setShowConfidence(!showConfidence)}
            data-testid="confidence-toggle"
          >
            <Icon name="bx-info-circle" />
            Confidence reference
            {showConfidence ? <Icon name="bx-chevron-up" /> : <Icon name="bx-chevron-down" />}
          </Button>

          {showConfidence && (
            <div className="mt-2 overflow-x-auto" data-testid="confidence-table">
              <table className="hub-table">
                <thead>
                  <tr>
                    <th>Samples/prompt/engine/week</th>
                    <th>Confidence</th>
                    <th>Accuracy</th>
                  </tr>
                </thead>
                <tbody>
                  {CONFIDENCE_TABLE.map((row) => (
                    <tr key={row.range}>
                      <td className="font-mono text-xs">{row.range}</td>
                      <td className="text-xs">{row.tier}</td>
                      <td className="text-xs">{row.accuracy}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Region>

      {/* All Brands Overview — the table region. Its four columns and their
          status cell carry both wiring contracts; see the note on the page
          root. */}
      <Region data-testid="brands-overview">
        <RegionHead
          title="All brands"
          caption="Active prompts, last collection and current status"
          action={
            brandRows.length > 0 ? (
              <span className="text-xs text-[var(--hub-color-ink-muted)]">
                {brandRows.length} brand{brandRows.length === 1 ? '' : 's'}
              </span>
            ) : undefined
          }
        />

        {brandRowsLoading ? (
          <div className="flex items-center justify-center py-8">
            <Spinner className="text-violet-500" />
          </div>
        ) : brandRows.length === 0 ? (
          <div className="flex flex-col items-center py-8 text-sm text-[var(--hub-color-ink-muted)]">
            No brands configured
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="hub-table">
              <thead>
                <tr>
                  <th>Brand name</th>
                  <th className="is-num">Active prompts</th>
                  <th>Last collection</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {brandRows.map((row) => (
                  <tr key={row.id} data-testid={`brand-row-${row.id}`}>
                    <td className="font-medium">{row.name}</td>
                    <td className="is-num">{row.activePrompts}</td>
                    <td className="text-[var(--hub-color-ink-muted)]">
                      {row.lastCollection
                        ? new Date(row.lastCollection).toLocaleString(undefined, {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : <span className="text-[var(--hub-color-ink-subtle)]">Never</span>}
                    </td>
                    <td>
                      <StatusBadge status={row.status} scheduledAt={row.scheduledAt} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Region>

      {/* Cost preview — tenant-wide (all brands). A bare direct child rather
          than a Region: it already carries its own padding and accent rule, so
          wrapping it would nest a padded box inside a padded box. The hairline
          above it comes from `.hub-page--dashboard > * + *` either way. */}
      <CostPreview preview={preview} loading={previewLoading} />
    </div>
  );
}

function StatusBadge({
  status,
  scheduledAt,
}: {
  status: BrandOverviewRow['status'];
  scheduledAt?: string | null;
}) {
  // Spec 235's inherited tone mapping: blue → info, emerald → ok, amber →
  // warn, red → fail, slate → not a tone but a bare chip. Every chip keeps
  // its word, which is also what keeps gates 140.A.3 and 147.A.2 honest —
  // 'stalled' and 'scheduled' are wiring, not decoration, and the testids
  // brand-status-stalled / brand-status-scheduled ride on the chips below.
  switch (status) {
    case 'running':
      return (
        <Chip tone="info">
          <Spinner  /> Running
        </Chip>
      );
    case 'scheduled': {
      const when = scheduledAt
        ? new Date(scheduledAt).toLocaleString(undefined, {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })
        : null;
      return (
        <Chip
          title={when ? `Scheduled to run ${when}` : 'Scheduled to run later'}
          data-testid="brand-status-scheduled"
        >
          <Icon name="bx-calendar-event" /> Scheduled
        </Chip>
      );
    }
    case 'completed':
      return (
        <Chip tone="ok">
          <Icon name="bx-check-circle" /> Completed
        </Chip>
      );
    case 'stalled':
      return (
        <Chip
          tone="warn"
          title="Collection stalled — the worker hasn't advanced this run. (If this persists, the background worker may not be firing.)"
          data-testid="brand-status-stalled"
        >
          <Icon name="bx-error" /> Stalled
        </Chip>
      );
    case 'failed':
      return (
        <Chip tone="fail">
          <Icon name="bx-error-circle" /> Failed
        </Chip>
      );
    default:
      return <Chip>Idle</Chip>;
  }
}
